import { Link, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleContext, ArticleContextInterface } from './ArticleContext';

interface TableOfContentsProps {
  readonly tableOfContents: Element[];
  readonly title?: string;
  readonly hasFootnotes: boolean;
  onLinkClick: () => void;
}

export default function TableOfContents({
  tableOfContents,
  title,
  hasFootnotes = false,
  onLinkClick,
}: Readonly<TableOfContentsProps>) {
  /* @NOTE: Should the list be turned into an ordered list without showing the
  numbers of the list? Does this have any effect on accessibility? */
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { activeId, observer, setActiveId } = useContext(
    ArticleContext,
  ) as ArticleContextInterface;

  useEffect(() => {
    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const chapterId = entry.target.getAttribute('data-chapter-id');

        if (entry?.isIntersecting && chapterId !== null) {
          setActiveId(chapterId);
        }
      });
    };

    observer.current = new IntersectionObserver(handleObserver, {
      root: null, // relative to the viewport
      rootMargin: '0px', // no margin
      threshold: 1, // callback if target is visible completely
    });

    document
      .querySelectorAll('[data-has-chapter-id="true"]')
      .forEach((element) => {
        observer?.current?.observe(element);
      });

    return () => {
      return observer.current?.disconnect();
    };
  }, [setActiveId, observer]);

  const allItems = tableOfContents.map((element, index) => {
    const isTitle = element.tagName === 'h1' && title;
    return {
      isTitle,
      key: index,
      text: isTitle ? title : element.textContent,
      id: encodeURIComponent(isTitle ? title : (element.textContent ?? '')),
      variant: 'body2',
      padding: isTitle
        ? [0, 2, 3, 2]
        : [0.5, 2, 0.5, parseInt(element.tagName.charAt(1), 10) * 2 - 2],
      sx: isTitle
        ? {
            fontFamily: [
              'Hanken Grotesk',
              'Helvetica',
              'Arial',
              'sans-serif',
            ].join(','),
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '130%',
          }
        : {},
    };
  });

  if (hasFootnotes) {
    allItems.push({
      isTitle: false,
      key: allItems.length,
      text: t('article.footnotes'),
      id: encodeURIComponent('footnotes'),
      variant: 'body2',
      padding: [0.5, 2, 0.5, 2],
      sx: {},
    });
  }

  return (
    <>
      {!isMobile ? (
        <Stack>
          {allItems.map((item) => {
            return (
              <Link
                key={'table-of-content-' + item.key}
                href={'#' + item.id}
                variant={item.isTitle ? undefined : 'body2'}
                underline='hover'
                color='text.primary'
                onClick={onLinkClick}
                sx={[
                  {
                    paddingTop: item.padding[0],
                    paddingRight: item.padding[1],
                    paddingBottom: item.padding[2],
                    paddingLeft: item.padding[3],
                    fontWeight: activeId === item.id ? 'bold' : undefined,
                  },
                  item.sx,
                ]}
              >
                {item.text}
              </Link>
            );
          })}
        </Stack>
      ) : (
        <Stack sx={{ overflowY: 'auto', maxHeight: '450px', paddingBottom: 4 }}>
          {allItems.map((item) => {
            return (
              <Link
                key={'table-of-content-' + item.key}
                href={'#' + item.id}
                variant={item.isTitle ? undefined : 'body2'}
                underline='hover'
                color='text.primary'
                onClick={onLinkClick}
                sx={[
                  {
                    paddingY: 3,
                    fontWeight: activeId === item.id ? 'bold' : undefined,
                  },
                  item.sx,
                ]}
              >
                {item.text}
              </Link>
            );
          })}
        </Stack>
      )}
    </>
  );
}
